/**
 * SmartChoiceDemo - Interactive testing page for SmartChoiceWidget
 *
 * Access via:
 * - http://localhost:3000/smart-choice-widget (locally)
 * - https://findcare-<ENV>.uhc.com/smart-choice-widget
 *
 *
 * This component provides a storybook-like interface to test various
 * configurations of the Smart Choice Widget before CEX integration.
 *
 * Features:
 * - Persistent settings (saved to localStorage)
 * - Mock recommendation preferences state presets
 * - Real-time widget preview
 * - Apply mock preferences via GraphQL API
 */
import { useForm } from '@abyss/web/hooks/useForm';
import { Accordion } from '@abyss/web/ui/Accordion';
import { Button } from '@abyss/web/ui/Button';
import { FormProvider } from '@abyss/web/ui/FormProvider';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Skeleton } from '@abyss/web/ui/Skeleton';
import { Tooltip } from '@abyss/web/ui/Tooltip';
import { CareCategories } from 'parcels/src/common/Constants';
import {
  SMART_CHOICE_EVENTS,
  SmartChoicePreferencesEventDetail,
  SmartChoiceSimpleEventDetail,
  SmartChoiceWidgetApp,
} from 'parcels/src/frontends/SmartChoiceWidget';
import { useHsidUuid } from 'parcels/src/hooks/useHsidUuid';
import { useSaveRecommendationPreferences } from 'parcels/src/hooks/useSaveRecommendationPreferencesStatus';
import { StoreKeys } from 'parcels/src/hooks/useStore/state';
import { useStore } from 'parcels/src/hooks/useStore/useStore';
import { useChipStore } from 'parcels/src/store/useChipStore';
import React, { useCallback, useEffect, useState } from 'react';

import {
  LoadingStateType,
  MockPreferences,
  defaultSettings,
  loadingStateConfigs,
  mockPreferenceDescriptions,
  mockPresets,
  widgetPropDescriptions,
} from './constants';
import {
  ButtonGroup,
  ClearLogButton,
  Container,
  ControlPanel,
  EventLogAction,
  EventLogEmpty,
  EventLogHeader,
  EventLogItem,
  EventLogList,
  EventLogPayload,
  EventLogSection,
  EventLogTimestamp,
  EventLogTitle,
  FormField,
  FormFieldLabel,
  MockStateBadge,
  MockStateBadgeContainer,
  MockStateDescription,
  MockStateSection,
  MockStateSubsection,
  MockStateSubsectionTitle,
  MockStateSummary,
  MockStateSummaryTitle,
  MockStateTitle,
  PersistenceIndicator,
  PresetButton,
  PresetButtonGroup,
  PropsDisplay,
  RadioGroupWrapper,
  Section,
  SectionTitle,
  SmallText,
  StyledHeading,
  StyledRadio,
  StyledRadioGroup,
  StyledText,
  StyledTextInput,
  StyledToggleSwitch,
  SubSection,
  ToggleGroup,
  WidgetContainer,
  WidgetWrapper,
} from './SmartChoiceDemo.styled';
import {
  buildPortalData,
  getPortalDefaults,
  loadSavedSettings,
  saveSettings,
} from './utils';

// InfoTooltip component using Abyss Tooltip with left positioning to avoid sidebar overflow
const InfoTooltip = ({ text }: { text: string }) => (
  <Tooltip align="center" asChild={false} content={text} position="left">
    <IconSymbol color="$core.color.turquoise.100" icon="info" size={20} />
  </Tooltip>
);

export const SmartChoiceDemo = () => {
  // Load initial state from localStorage
  const initialSettings = loadSavedSettings();

  // Initialize chip store with a valid value so coachmark can display
  // The SmartChoiceWidget checks chipValue to determine if coachmark should show
  const { setChipValue } = useChipStore();

  // Widget Props State
  // isSuperUser is always true in the demo - required for testing/demo environment
  // to ensure preferences are stored with the superUser suffix and don't affect real user data
  const isSuperUser = true;
  const [isFullScreenCompareSmartChoice, setIsFullScreenCompareSmartChoice] =
    useState<boolean>(initialSettings.isFullScreenCompareSmartChoice);
  const [isMixedResultsPage, setIsMixedResultsPage] = useState<boolean>(
    initialSettings.isMixedResultsPage
  );
  const [showChoosePCPHeader, setShowChoosePCPHeader] = useState<boolean>(
    initialSettings.showChoosePCPHeader
  );
  const [showMemberSelection, setShowMemberSelection] = useState<boolean>(
    initialSettings.showMemberSelection
  );
  const [memberId, setMemberId] = useState<string>(
    initialSettings.memberId || 'demo-test-user-00000000'
  );

  // Details Page State
  const [showDetailsPage, setShowDetailsPage] = useState<boolean>(false);
  const [providerId, setProviderId] = useState<string>('006576501');
  const [locationId, setLocationId] = useState<string>(
    '25980B5B7AE0964C48FB08B4836EFD99'
  );
  const [providerType, setProviderType] = useState<string>('Practitioner');
  const [latitude, setLatitude] = useState<string>('41.9033');
  const [longitude, setLongitude] = useState<string>('-87.6336');
  const [reciprocityId, setReciprocityId] = useState<string>('10264');
  const [coverageType, setCoverageType] = useState<string>('M');
  const [claimSystemTypeCode, setClaimSystemTypeCode] = useState<string>('01');
  const [lob, setLob] = useState<string>('E&I');
  const [eligibilitySystemTypeCode, setEligibilitySystemTypeCode] =
    useState<string>('01');
  const [planVariationCode, setPlanVariationCode] = useState<string>('2281');
  const [planYear, setPlanYear] = useState<string>('2026');
  const [policyId, setPolicyId] = useState<string>('168504');
  const [membershipCategory, setMembershipCategory] = useState<string>('');
  const [population, setPopulation] = useState<string>('UNET');
  const [clientName, setClientName] = useState<string>('CEX');
  const [billingCodes, setBillingCodes] = useState<string>('99202');
  const [cesProductCode, setCesProductCode] = useState<string>('');
  const [marketType, setMarketType] = useState<string>('');

  // PreferencesForm Submit Button Testing State
  const [submitButtonLabel, setSubmitButtonLabel] = useState<string>('');
  const [
    preferencesFormFromProviderDetail,
    setPreferencesFormFromProviderDetail,
  ] = useState<boolean>(false);

  // Portal Configuration State
  const [portalName, setPortalName] = useState<string>(
    initialSettings.portalName
  );
  const [theme, setTheme] = useState<string>(initialSettings.theme);
  const [language, setLanguage] = useState<string>(initialSettings.language);
  const [basePath, setBasePath] = useState<string>(initialSettings.basePath);
  const [useFallbackLob, setUseFallbackLob] = useState<boolean>(
    initialSettings.useFallbackLob ?? false
  );

  // Mock Recommendation Preferences State - using useForm for form management
  const [mockPreferences, setMockPreferences] = useState<MockPreferences>(
    initialSettings.mockPreferences
  );

  // Initialize useForm for mock preferences with react-hook-form integration
  const mockPreferencesForm = useForm({
    defaultValues: initialSettings.mockPreferences,
  });

  // Widget key to force re-render
  const [widgetKey, setWidgetKey] = useState<number>(0);

  // Loading state simulation for debugging skeleton states
  const [simulatedLoadingState, setSimulatedLoadingState] =
    useState<LoadingStateType>('none');

  // Persistence indicator
  const [lastSaved, setLastSaved] = useState<string | null>(null);

  // State for tracking mock preferences API calls
  const [isApplyingPreferences, setIsApplyingPreferences] =
    useState<boolean>(false);
  const [preferencesApplyStatus, setPreferencesApplyStatus] = useState<
    string | null
  >(null);

  // State to track events received from the widget (demonstrates parent app integration)
  const [lastWidgetEvent, setLastWidgetEvent] = useState<{
    type: string;
    timestamp: string;
    source: string;
    preferences?: SmartChoicePreferencesEventDetail['preferences'];
  } | null>(null);

  // Event log to track all widget events (simulates Redux action log)
  const [eventLog, setEventLog] = useState<
    Array<{
      id: number;
      type: string;
      timestamp: string;
      source: string;
      action: string; // Redux-style action name
      payload: Record<string, unknown>;
    }>
  >([]);

  // Get user ID for preferences API
  // Priority: 1) memberId prop (from input field), 2) store hsidUuid, 3) fallback test UUID
  const hsidUuidFromStore = useHsidUuid();
  // Fallback test UUID - used when no other ID is available (demo/testing context)
  const TEST_USER_UUID = 'demo-test-user-00000000';
  // Use memberId from input if provided, otherwise try store, then fallback
  const effectiveUserId = memberId || hsidUuidFromStore || TEST_USER_UUID;

  // Get the store setter to clear recommendation preferences from Zustand store
  const setRecommendationPreferences = useStore(
    StoreKeys.SET_RECOMMENDATION_PREFERENCES
  );

  const addEventToLog = (
    type: string,
    source: string,
    action: string,
    payload: Record<string, unknown>
  ) => {
    // Use timestamp + random suffix to ensure unique IDs even if multiple events fire simultaneously
    const uniqueId = Date.now() + Math.random();
    setEventLog((prev) => [
      {
        id: uniqueId,
        type,
        timestamp: new Date().toISOString(),
        source,
        action,
        payload,
      },
      ...prev.slice(0, 49), // Keep last 50 events
    ]);
  };

  useEffect(() => {
    // Set chipValue to 'All' which is a valid category for displaying coachmark
    // This is necessary because shouldDisplayCoachMarkBasedOnChipValue() checks for valid chip values
    setChipValue(CareCategories.ALL);
  }, [setChipValue]);

  // Sync form values with mockPreferences state when form changes
  useEffect(() => {
    const subscription = mockPreferencesForm.watch((values) => {
      setMockPreferences(values as MockPreferences);
    });
    return () => subscription.unsubscribe();
  }, [mockPreferencesForm]);

  // Setups the Close drawer event handlers for the details page view
  useEffect(() => {
    const handleCloseDetailsDrawer = (
      e: CustomEvent<SmartChoiceSimpleEventDetail>
    ) => {
      const { operation, timestamp } = e.detail;

      setLastWidgetEvent({
        type: 'DETAILS_CLOSED',
        timestamp,
        source: operation,
      });

      // Add to event log (simulates Redux action dispatch)
      addEventToLog('DETAILS_CLOSED', operation, 'smartChoice/detailsClosed', {
        preferences: {},
        userId: e.detail.userId,
        isSuperUser: e.detail.isSuperUser,
      });

      setShowDetailsPage(false);
    };

    document.addEventListener(
      SMART_CHOICE_EVENTS.DETAILS_CLOSED,
      handleCloseDetailsDrawer as EventListener
    );

    return () => {
      document.removeEventListener(
        SMART_CHOICE_EVENTS.DETAILS_CLOSED,
        handleCloseDetailsDrawer as EventListener
      );
    };
  }, [providerId]);

  // Listen for Smart Choice widget events (demonstrates how CEX would integrate)
  // This is how a parent application like CEX would save preferences to their Redux store
  // Also used to sync the demo's toggle controls with the actual widget state
  useEffect(() => {
    const handlePreferencesLoaded = (
      e: CustomEvent<SmartChoicePreferencesEventDetail>
    ) => {
      const { preferences, timestamp, source } = e.detail;

      setLastWidgetEvent({
        type: 'PREFERENCES_LOADED',
        timestamp,
        source,
        preferences,
      });

      // Add to event log (simulates Redux action dispatch)
      addEventToLog(
        'PREFERENCES_LOADED',
        source,
        'smartChoice/preferencesLoaded',
        {
          preferences,
          userId: e.detail.userId,
          isSuperUser: e.detail.isSuperUser,
        }
      );

      // Sync the demo's toggle controls with the widget's preferences
      // This replaces the need for a separate API call in the demo
      const syncedPreferences: MockPreferences = {
        hasDismissedCoachMark: preferences.hasDismissedCoachMark === 'y',
        hasDismissedCoachMarkWeb: preferences.hasDismissedCoachMarkWeb === 'y',
        hasDismissedPopover: preferences.hasDismissedPopover === 'y',
        hasSetPreferences: preferences.hasSetPreferences === 'y',
        hasDefaultPreferences: preferences.hasDefaultPreferences === 'y',
      };
      setMockPreferences(syncedPreferences);
      mockPreferencesForm.reset(syncedPreferences);

      // In CEX, you would dispatch to Redux here:
      // dispatch(setSmartChoicePreferences(preferences));
    };

    const handlePreferencesSaved = (
      e: CustomEvent<SmartChoicePreferencesEventDetail>
    ) => {
      const { preferences, timestamp, source } = e.detail;

      setLastWidgetEvent({
        type: 'PREFERENCES_SAVED',
        timestamp,
        source,
        preferences,
      });

      // Add to event log (simulates Redux action dispatch)
      addEventToLog(
        'PREFERENCES_SAVED',
        source,
        'smartChoice/preferencesSaved',
        {
          preferences,
          userId: e.detail.userId,
          isSuperUser: e.detail.isSuperUser,
        }
      );

      // Sync the demo's toggle controls when preferences are saved
      const syncedPreferences: MockPreferences = {
        hasDismissedCoachMark: preferences.hasDismissedCoachMark === 'y',
        hasDismissedCoachMarkWeb: preferences.hasDismissedCoachMarkWeb === 'y',
        hasDismissedPopover: preferences.hasDismissedPopover === 'y',
        hasSetPreferences: preferences.hasSetPreferences === 'y',
        hasDefaultPreferences: preferences.hasDefaultPreferences === 'y',
      };
      setMockPreferences(syncedPreferences);
      mockPreferencesForm.reset(syncedPreferences);

      // In CEX, you would dispatch to Redux here:
      // dispatch(setSmartChoicePreferences(preferences));
    };

    document.addEventListener(
      SMART_CHOICE_EVENTS.PREFERENCES_LOADED,
      handlePreferencesLoaded as EventListener
    );
    document.addEventListener(
      SMART_CHOICE_EVENTS.PREFERENCES_SAVED,
      handlePreferencesSaved as EventListener
    );

    return () => {
      document.removeEventListener(
        SMART_CHOICE_EVENTS.PREFERENCES_LOADED,
        handlePreferencesLoaded as EventListener
      );
      document.removeEventListener(
        SMART_CHOICE_EVENTS.PREFERENCES_SAVED,
        handlePreferencesSaved as EventListener
      );
    };
  }, [mockPreferencesForm]);

  // Hook for saving recommendation preferences to backend
  const [saveResult, saveRecommendationPreferences] =
    useSaveRecommendationPreferences({
      handleError: () => {
        setIsApplyingPreferences(false);
        setPreferencesApplyStatus(
          '❌ Failed to apply preferences. Check console for details.'
        );
        setTimeout(() => setPreferencesApplyStatus(null), 5000);
      },
    });

  // NOTE: Demo intentionally does not call the GET here; it reloads to follow the
  // same behavior as the real widget (widget will perform its own GET on mount).

  // Handle save success via useEffect to ensure we have access to the response data
  // This solves the UUID mismatch issue where GET might return a different record
  useEffect(() => {
    if (
      saveResult?.data?.saveRecommendationPreferencesStatus &&
      isApplyingPreferences
    ) {
      const savedData = saveResult.data.saveRecommendationPreferencesStatus;

      // Update the shared store with the save response so the demo UI reflects
      // the newly saved values. In production the widget itself performs the
      // authoritative GET after save; to mimic that behavior and keep the demo
      // implementation minimal/faithful, reload the page so the widget will
      // re-initialize and fetch the authoritative RecommendationPreferencesStatus.
      setRecommendationPreferences(savedData);

      setIsApplyingPreferences(false);
      setPreferencesApplyStatus(
        '✅ Preferences saved — reloading to reflect API state'
      );

      // Small delay for UX before reload so the success state is visible briefly
      setTimeout(() => {
        // Reload the full page so the widget re-reads from the API (source of truth)
        window.location.reload();
      }, 700);
    }
  }, [saveResult, isApplyingPreferences, setRecommendationPreferences]);

  // Helper function to clear Smart Choice related session storage
  // This ensures the widget reads fresh state after applying preferences
  // Note: We don't clear Zustand store here - it will be updated with save response data
  const clearSmartChoiceSessionStorage = useCallback(() => {
    sessionStorage.removeItem('hasDismissedPopover');
    sessionStorage.removeItem('hasDismissedCoachMarkWeb');
    sessionStorage.removeItem('smartChoicePopoverDismissed');
  }, []);

  // Function to apply mock preferences via GraphQL API
  const applyMockPreferencesToBackend = useCallback(() => {
    setIsApplyingPreferences(true);
    setPreferencesApplyStatus('⏳ Applying preferences...');

    // Clear session storage so widget reads fresh state from API
    clearSmartChoiceSessionStorage();

    // Build uuid with superUser suffix if applicable (matches expected API format)
    const uuidWithSuffix = `${effectiveUserId}${
      isSuperUser ? '-superUser' : ''
    }`;

    // Base payload with state flags
    const basePayload = {
      // Include both uuid (with suffix) and userId (without suffix) for API compatibility
      uuid: uuidWithSuffix,
      userId: effectiveUserId,
      hasDismissedCoachMark: mockPreferences.hasDismissedCoachMark ? 'y' : 'n',
      hasDismissedCoachMarkWeb: mockPreferences.hasDismissedCoachMarkWeb
        ? 'y'
        : 'n',
      hasDismissedPopover: mockPreferences.hasDismissedPopover ? 'y' : 'n',
      hasSetPreferences: mockPreferences.hasSetPreferences ? 'y' : 'n',
      isSuperUser,
    };

    // When hasSetPreferences is 'y', the API requires all preference fields
    // Include default values for required preference fields
    const preferenceFields = mockPreferences.hasSetPreferences
      ? {
          evngApptAvbl: 'n', // Evening appointments available
          wkndApptAvbl: 'n', // Weekend appointments available
          onlineApptSchdlAvbl: 'n', // Online appointment scheduling available
          virtualApptAvbl: 'n', // Virtual appointments available
          distance: '', // Distance (empty = no preference)
          languagesSpoken: '', // Languages spoken (empty = no preference)
          providerGender: '', // Provider gender preference (empty = no preference)
          providerBoardCertStatus: 'n', // Board certification status
        }
      : {};

    const payload = {
      ...basePayload,
      ...preferenceFields,
    };

    saveRecommendationPreferences({
      variables: payload,
    });
  }, [
    mockPreferences,
    effectiveUserId,
    isSuperUser,
    saveRecommendationPreferences,
    clearSmartChoiceSessionStorage,
  ]);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    const settings = {
      isSuperUser,
      isFullScreenCompareSmartChoice,
      isMixedResultsPage,
      showChoosePCPHeader,
      showMemberSelection,
      memberId,
      portalName,
      theme,
      language,
      basePath,
      useFallbackLob,
      mockPreferences,
    };
    const timestamp = saveSettings(settings);
    if (timestamp) {
      setLastSaved(timestamp);
    }
  }, [
    isSuperUser,
    isFullScreenCompareSmartChoice,
    isMixedResultsPage,
    showChoosePCPHeader,
    showMemberSelection,
    memberId,
    portalName,
    theme,
    language,
    basePath,
    useFallbackLob,
    mockPreferences,
  ]);

  // Build the current props object for display
  const getCurrentProps = useCallback(
    () => ({
      'is-super-user': isSuperUser,
      'is-full-screen-compare-smart-choice': isFullScreenCompareSmartChoice,
      'is-mixed-results-page': isMixedResultsPage,
      'show-choose-pcp-header': showChoosePCPHeader,
      'show-member-selection': showMemberSelection,
      'member-id': memberId || undefined,
      'portal-data': buildPortalData(
        portalName,
        basePath,
        theme,
        language,
        useFallbackLob
      ),
      'mock-preferences': mockPreferences,
    }),
    [
      isSuperUser,
      isFullScreenCompareSmartChoice,
      isMixedResultsPage,
      showChoosePCPHeader,
      showMemberSelection,
      memberId,
      portalName,
      basePath,
      theme,
      language,
      mockPreferences,
    ]
  );

  // Apply a mock preset (updates local state and optionally calls backend)
  const applyPreset = (presetKey: string, applyToBackend: boolean = true) => {
    const preset = mockPresets[presetKey];
    if (preset) {
      setMockPreferences(preset.preferences);
      mockPreferencesForm.reset(preset.preferences);

      // Clear session storage so widget reads fresh state from API
      clearSmartChoiceSessionStorage();

      // Apply to backend if requested
      if (applyToBackend) {
        setIsApplyingPreferences(true);
        setPreferencesApplyStatus('⏳ Applying preset preferences...');

        // Build uuid with superUser suffix if applicable
        const uuidWithSuffix = `${effectiveUserId}${
          isSuperUser ? '-superUser' : ''
        }`;

        // Base payload with state flags
        const basePayload = {
          uuid: uuidWithSuffix,
          userId: effectiveUserId,
          hasDismissedCoachMark: preset.preferences.hasDismissedCoachMark
            ? 'y'
            : 'n',
          hasDismissedCoachMarkWeb: preset.preferences.hasDismissedCoachMarkWeb
            ? 'y'
            : 'n',
          hasDismissedPopover: preset.preferences.hasDismissedPopover
            ? 'y'
            : 'n',
          hasSetPreferences: preset.preferences.hasSetPreferences ? 'y' : 'n',
          isSuperUser,
        };

        // When hasSetPreferences is 'y', include required preference fields
        const preferenceFields = preset.preferences.hasSetPreferences
          ? {
              evngApptAvbl: 'n',
              wkndApptAvbl: 'n',
              onlineApptSchdlAvbl: 'n',
              virtualApptAvbl: 'n',
              distance: '',
              languagesSpoken: '',
              providerGender: '',
              providerBoardCertStatus: 'n',
            }
          : {};

        const payload = {
          ...basePayload,
          ...preferenceFields,
        };

        saveRecommendationPreferences({ variables: payload });
      } else {
        setWidgetKey((prev) => prev + 1);
      }
    }
  };

  // Handle portal name change with auto-defaults
  const handlePortalChange = (value: string) => {
    setPortalName(value);
    const defaults = getPortalDefaults(value);
    setTheme(defaults.theme);
    setBasePath(defaults.basePath);
    setUseFallbackLob(defaults.useFallbackLob);
    // Force widget to re-render with new portal configuration
    setWidgetKey((prev) => prev + 1);
  };

  const portalData = buildPortalData(
    portalName,
    basePath,
    theme,
    language,
    useFallbackLob
  );

  return (
    <Container>
      {/* Control Panel - Left Side */}
      <ControlPanel>
        <StyledHeading offset={4}>Smart Choice Widget Demo</StyledHeading>
        <StyledText>
          Configure widget props and test different scenarios
        </StyledText>

        {/* Persistence Indicator */}
        {lastSaved && (
          <PersistenceIndicator>
            💾 Settings auto-saved at {lastSaved}
          </PersistenceIndicator>
        )}

        {/* Mock Recommendation Preferences Section wrapped in Accordion (open by default, collapsible) */}
        <Accordion defaultValue="mock-preferences" isCollapsible type="single">
          <Accordion.Item value="mock-preferences">
            <Accordion.Trigger>
              <Accordion.Header>
                <MockStateTitle>
                  🧪 Mock Recommendation Preferences
                  {lastWidgetEvent && ' ✓ Synced via Widget Events'}
                </MockStateTitle>
              </Accordion.Header>
            </Accordion.Trigger>
            <Accordion.Content>
              <MockStateSection>
                <MockStateDescription>
                  Set mock preferences and apply them to the backend. Saved
                  locally; apply to affect the widget.
                </MockStateDescription>

                {/* Quick Presets Subsection */}
                <MockStateSubsection>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '12px',
                    }}
                  >
                    <MockStateSubsectionTitle css={{ marginBottom: 0 }}>
                      ⚡ Quick Presets
                    </MockStateSubsectionTitle>
                    <InfoTooltip text="Click a preset to instantly apply common user states. These presets update the Individual Preference Controls below." />
                  </div>
                  <PresetButtonGroup>
                    {Object.entries(mockPresets).map(([key, preset]) => (
                      <PresetButton
                        key={key}
                        onClick={() => applyPreset(key)}
                        size="sm"
                        title={preset.description}
                        variant="outline"
                      >
                        {preset.label}
                      </PresetButton>
                    ))}
                  </PresetButtonGroup>
                </MockStateSubsection>

                {/* Individual Preference Controls Subsection */}
                <MockStateSubsection>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '12px',
                    }}
                  >
                    <MockStateSubsectionTitle css={{ marginBottom: 0 }}>
                      🎛️ Individual Preference Controls
                    </MockStateSubsectionTitle>
                    <InfoTooltip text="Fine-tune individual preference flags. Use the buttons below to apply these settings to the backend or clear session storage." />
                  </div>

                  {/* Individual Mock Preference Toggles - using FormProvider for form management */}
                  <FormProvider state={mockPreferencesForm}>
                    <ToggleGroup>
                      {(
                        Object.keys(defaultSettings.mockPreferences) as Array<
                          keyof MockPreferences
                        >
                      ).map((key) => (
                        <StyledToggleSwitch
                          helper={mockPreferenceDescriptions[key]}
                          key={key}
                          label={key}
                          model={key}
                        />
                      ))}
                    </ToggleGroup>
                  </FormProvider>

                  {/* Apply Preferences Button */}
                  <ButtonGroup style={{ marginTop: '16px', gap: '8px' }}>
                    <Button
                      data-testid="apply-preferences-button"
                      isLoading={isApplyingPreferences}
                      onClick={applyMockPreferencesToBackend}
                      variant="solid"
                    >
                      📤 Apply Preferences to Backend
                    </Button>
                    <Button
                      data-testid="clear-session-storage-button"
                      onClick={() => {
                        clearSmartChoiceSessionStorage();
                        setWidgetKey((prev) => prev + 1);
                        setPreferencesApplyStatus(
                          '🧹 Session storage cleared! Widget refreshed.'
                        );
                        setTimeout(() => setPreferencesApplyStatus(null), 3000);
                      }}
                      variant="outline"
                    >
                      🧹 Clear Session Storage
                    </Button>
                  </ButtonGroup>

                  {/* Status Indicator */}
                  {preferencesApplyStatus && (
                    <SmallText css={{ marginTop: '8px', fontWeight: 500 }}>
                      {preferencesApplyStatus}
                    </SmallText>
                  )}
                </MockStateSubsection>

                {/* Compact 'How it works' accordion to reduce sidebar space */}
                <Accordion defaultValue={undefined} isCollapsible type="single">
                  <Accordion.Item value="how-it-works">
                    <Accordion.Trigger>
                      <Accordion.Header>ℹ️ How it works</Accordion.Header>
                    </Accordion.Trigger>
                    <Accordion.Content>
                      <SmallText
                        css={{
                          marginTop: '8px',
                          color: '#6b7280',
                          lineHeight: '1.4',
                        }}
                      >
                        <ul style={{ margin: 0, paddingLeft: 16 }}>
                          <li>
                            "Apply Preferences to Backend" calls the
                            SaveRecommendationPreferencesStatus GraphQL
                            mutation.
                          </li>
                          <li>
                            Session storage is automatically cleared when
                            applying preferences.
                          </li>
                          <li>
                            The widget checks both session storage and API
                            response for coachmark/popover visibility.
                          </li>
                          <li>
                            Use "Clear Session Storage" if the widget isn't
                            reflecting API state correctly.
                          </li>
                        </ul>
                      </SmallText>
                    </Accordion.Content>
                  </Accordion.Item>
                </Accordion>
              </MockStateSection>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion>

        {/* Widget Props Section (wrapped in an Accordion; closed by default) */}
        <Accordion defaultValue={undefined} isCollapsible type="single">
          <Accordion.Item value="widget-props">
            <Accordion.Trigger>
              <Accordion.Header>
                <SectionTitle>Widget Props</SectionTitle>
              </Accordion.Header>
            </Accordion.Trigger>
            <Accordion.Content>
              <Section>
                <FormField>
                  <StyledToggleSwitch
                    data-testid="is-super-user-toggle"
                    helper={widgetPropDescriptions.isSuperUser}
                    isChecked={isSuperUser}
                    isDisabled
                    label="is-super-user"
                  />
                  <SmallText css={{ color: '#6b7280', marginTop: '4px' }}>
                    🔒 Always enabled in demo environment. This ensures test
                    preferences are stored separately (with
                    &quot;-superUser&quot; UUID suffix) and don&apos;t affect
                    real user data. In production, CEX controls this based on
                    user roles.
                  </SmallText>
                </FormField>

                <FormField>
                  <StyledToggleSwitch
                    data-testid="is-full-screen-toggle"
                    helper={
                      widgetPropDescriptions.isFullScreenCompareSmartChoice
                    }
                    isChecked={isFullScreenCompareSmartChoice}
                    label="is-full-screen-compare-smart-choice"
                    onChange={(e) =>
                      setIsFullScreenCompareSmartChoice(e.target.checked)
                    }
                  />
                  {isFullScreenCompareSmartChoice && (
                    <SmallText css={{ color: '#dc2626', marginTop: '4px' }}>
                      ⚠️ Widget content is hidden when this is enabled. This
                      prop is designed for use within PSXHeader context where
                      other components handle the full-screen compare display.
                    </SmallText>
                  )}
                </FormField>

                <FormField>
                  <StyledToggleSwitch
                    data-testid="is-mixed-results-toggle"
                    helper={widgetPropDescriptions.isMixedResultsPage}
                    isChecked={isMixedResultsPage}
                    label="is-mixed-results-page"
                    onChange={(e) => setIsMixedResultsPage(e.target.checked)}
                  />
                </FormField>

                <FormField>
                  <StyledToggleSwitch
                    data-testid="show-pcp-header-toggle"
                    helper={widgetPropDescriptions.showChoosePCPHeader}
                    isChecked={showChoosePCPHeader}
                    label="show-choose-pcp-header"
                    onChange={(e) => setShowChoosePCPHeader(e.target.checked)}
                  />
                </FormField>

                <FormField>
                  <StyledToggleSwitch
                    data-testid="show-member-selection-toggle"
                    helper={widgetPropDescriptions.showMemberSelection}
                    isChecked={showMemberSelection}
                    label="show-member-selection"
                    onChange={(e) => setShowMemberSelection(e.target.checked)}
                  />
                </FormField>

                <FormField>
                  <StyledTextInput
                    data-testid="member-id-input"
                    label="member-id"
                    onChange={(e) => setMemberId(e.target.value)}
                    placeholder="e.g., 793cf228-35a6-4e29-acd5-681d37e72ee8"
                    value={memberId}
                  />
                  <FormFieldLabel>
                    {widgetPropDescriptions.memberId}
                  </FormFieldLabel>
                </FormField>

                <SubSection>
                  <FormField>
                    <StyledTextInput
                      data-testid="submit-button-label-input"
                      helper="Direct string label for submit button. For CEX: pass the translated string from useTranslation(). For PSX: pass Lagoon message title."
                      label="submit-button-label"
                      onChange={(e) => setSubmitButtonLabel(e.target.value)}
                      placeholder="e.g., 'Search for Providers' or translated equivalent"
                      value={submitButtonLabel}
                    />
                  </FormField>

                  {/* Provider Detail Context - small control rendered inline (no accordion) */}
                  <FormField>
                    <StyledToggleSwitch
                      data-testid="preferences-from-provider-detail-toggle"
                      helper="When enabled, simulates opening preferences from provider detail page. Changes contextual label to 'Update provider score'."
                      isChecked={preferencesFormFromProviderDetail}
                      label="Simulate Provider Detail Context"
                      onChange={(e) => {
                        setPreferencesFormFromProviderDetail(e.target.checked);
                        setSubmitButtonLabel(
                          e.target.checked ? 'Update provider score' : ''
                        );
                      }}
                    />
                  </FormField>

                  {/* Compact explanation and scenarios implemented as an Accordion to reduce vertical space */}
                  <Accordion type="multiple">
                    <Accordion.Item value="button-label-order">
                      <Accordion.Trigger>
                        <Accordion.Header>
                          📋 Button Label Resolution
                        </Accordion.Header>
                      </Accordion.Trigger>
                      <Accordion.Content>
                        <div
                          style={{
                            display: 'flex',
                            gap: 12,
                            alignItems: 'flex-start',
                            flexDirection: 'column',
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <SmallText
                              css={{ fontWeight: 600, marginBottom: '6px' }}
                            >
                              Priority
                            </SmallText>
                            <SmallText
                              as="ul"
                              css={{
                                margin: 0,
                                paddingLeft: '18px',
                                '& li': { marginBottom: 4, fontSize: '13px' },
                              }}
                            >
                              <li>
                                <strong>1.</strong>{' '}
                                <span style={{ marginLeft: 6 }}>
                                  submit-button-label
                                </span>
                              </li>
                              <li>
                                <strong>2.</strong>{' '}
                                <span style={{ marginLeft: 6 }}>
                                  submit-button-label-key (i18n)
                                </span>
                              </li>
                              <li>
                                <strong>3.</strong>{' '}
                                <span style={{ marginLeft: 6 }}>
                                  Provider Detail Context (Update)
                                </span>
                              </li>
                              <li>
                                <strong>4.</strong>{' '}
                                <span style={{ marginLeft: 6 }}>
                                  Search Context (Update search results)
                                </span>
                              </li>
                              <li>
                                <strong>5.</strong>{' '}
                                <span style={{ marginLeft: 6 }}>
                                  Fallback (Search for providers)
                                </span>
                              </li>
                            </SmallText>
                          </div>
                          <div
                            style={{
                              width: 1,
                              background: '#e5e7eb',
                              margin: '0 8px',
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <SmallText
                              css={{ fontWeight: 600, marginBottom: '6px' }}
                            >
                              When to use
                            </SmallText>
                            <SmallText css={{ margin: 0, fontSize: '13px' }}>
                              Provide a direct string to override everything.
                              Use a key when you prefer i18n. Enable the
                              provider-detail toggle to simulate contextual
                              behavior.
                            </SmallText>
                          </div>
                        </div>
                      </Accordion.Content>
                    </Accordion.Item>

                    <Accordion.Item value="test-scenarios">
                      <Accordion.Trigger>
                        <Accordion.Header>🧪 Test Scenarios</Accordion.Header>
                      </Accordion.Trigger>
                      <Accordion.Content>
                        <div
                          style={{
                            display: 'flex',
                            gap: 8,
                            flexDirection: 'column',
                          }}
                        >
                          <SmallText as="div" css={{ margin: 0 }}>
                            <div style={{ fontWeight: 600, marginBottom: 6 }}>
                              Scenarios
                            </div>
                            <ul
                              style={{
                                margin: 0,
                                paddingLeft: 16,
                                fontSize: 13,
                              }}
                            >
                              <li>
                                <strong>1.</strong> Enter submit-button-label →
                                custom text
                              </li>
                              <li>
                                <strong>2.</strong> Clear label, enter
                                CEX.SAVE_PREFERENCES → translated text
                              </li>
                              <li>
                                <strong>3.</strong> Clear both, enable Provider
                                Detail → &quot;Update&quot;
                              </li>
                              <li>
                                <strong>4.</strong> Clear all → fallback
                                &quot;Search for providers&quot;
                              </li>
                            </ul>
                          </SmallText>
                          <SmallText as="div" css={{ margin: 0 }}>
                            <div style={{ fontWeight: 600, marginBottom: 6 }}>
                              Quick Tips
                            </div>
                            <div style={{ fontSize: 13 }}>
                              Keep label empty to rely on i18n keys or
                              contextual labels. Use the reset button to quickly
                              return to defaults.
                            </div>
                          </SmallText>
                        </div>
                      </Accordion.Content>
                    </Accordion.Item>
                  </Accordion>

                  {/* Quick Reset Button */}
                  <ButtonGroup css={{ marginTop: '20px' }}>
                    <Button
                      onClick={() => {
                        setSubmitButtonLabel('');
                        setSubmitButtonLabelKey('');
                        setPreferencesFormFromProviderDetail(false);
                      }}
                      size="sm"
                      variant="outline"
                    >
                      Reset All Button Settings
                    </Button>
                  </ButtonGroup>
                </SubSection>
              </Section>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion>

        {/* Details Page Section (wrapped in an Accordion; closed by default) */}
        <Accordion defaultValue={undefined} isCollapsible type="single">
          <Accordion.Item value="details-props">
            <Accordion.Trigger>
              <Accordion.Header>
                <SectionTitle>Details Page Props</SectionTitle>
              </Accordion.Header>
            </Accordion.Trigger>
            <Accordion.Content>
              <Section>
                <FormField>
                  <SmallText css={{ marginTop: '4px' }}>
                    Change the settings below to control the provider for the
                    smartchoice details page drawer. Normally the details page
                    is launched by clicking the (SC) icon that only appears on a
                    provider card. The details page drawer is only available in
                    lower environments.
                  </SmallText>
                </FormField>

                <SubSection>
                  <FormField>
                    <StyledTextInput
                      data-testid="provider-id-input"
                      label="provider-id"
                      onChange={(e) => setProviderId(e.target.value)}
                      placeholder={providerId}
                      value={providerId}
                    />
                  </FormField>
                  <FormField>
                    <StyledTextInput
                      data-testid="location-id-input"
                      label="location-id"
                      onChange={(e) => setLocationId(e.target.value)}
                      placeholder={locationId}
                      value={locationId}
                    />
                  </FormField>
                  <FormField>
                    <StyledTextInput
                      data-testid="provider-type-input"
                      label="provider-type"
                      onChange={(e) => setProviderType(e.target.value)}
                      placeholder={providerType}
                      value={providerType}
                    />
                  </FormField>
                  <FormField>
                    <StyledTextInput
                      data-testid="latitude-input"
                      label="latitude"
                      onChange={(e) => setLatitude(e.target.value)}
                      placeholder={latitude}
                      value={latitude}
                    />
                  </FormField>
                  <FormField>
                    <StyledTextInput
                      data-testid="longitude-input"
                      label="longitude"
                      onChange={(e) => setLongitude(e.target.value)}
                      placeholder={longitude}
                      value={longitude}
                    />
                  </FormField>
                  <FormField>
                    <StyledTextInput
                      data-testid="reciprocity-input"
                      label="reciprocity-id"
                      onChange={(e) => setReciprocityId(e.target.value)}
                      placeholder={reciprocityId}
                      value={reciprocityId}
                    />
                  </FormField>
                  <FormField>
                    <StyledTextInput
                      data-testid="coverage-type-input"
                      label="coverage-type"
                      onChange={(e) => setCoverageType(e.target.value)}
                      placeholder={coverageType}
                      value={coverageType}
                    />
                  </FormField>
                  <FormField>
                    <StyledTextInput
                      data-testid="claim-system-type-code-input"
                      label="claim-system-type-code"
                      onChange={(e) => setClaimSystemTypeCode(e.target.value)}
                      placeholder={claimSystemTypeCode}
                      value={claimSystemTypeCode}
                    />
                  </FormField>
                  <FormField>
                    <StyledTextInput
                      data-testid="lob-input"
                      label="lob"
                      onChange={(e) => setLob(e.target.value)}
                      placeholder={lob}
                      value={lob}
                    />
                  </FormField>
                  <FormField>
                    <StyledTextInput
                      data-testid="eligibility-system-type-code-input"
                      label="eligibility-system-type-code"
                      onChange={(e) =>
                        setEligibilitySystemTypeCode(e.target.value)
                      }
                      placeholder={eligibilitySystemTypeCode}
                      value={eligibilitySystemTypeCode}
                    />
                  </FormField>
                  <FormField>
                    <StyledTextInput
                      data-testid="plan-variation-code-input"
                      label="plan-variation-code"
                      onChange={(e) => setPlanVariationCode(e.target.value)}
                      placeholder={planVariationCode}
                      value={planVariationCode}
                    />
                  </FormField>
                  <FormField>
                    <StyledTextInput
                      data-testid="plan-year-input"
                      label="plan-year"
                      onChange={(e) => setPlanYear(e.target.value)}
                      placeholder={planYear}
                      value={planYear}
                    />
                  </FormField>
                  <FormField>
                    <StyledTextInput
                      data-testid="policy-id-input"
                      label="policy-id"
                      onChange={(e) => setPolicyId(e.target.value)}
                      placeholder={policyId}
                      value={policyId}
                    />
                  </FormField>
                  <FormField>
                    <StyledTextInput
                      data-testid="membership-category-input"
                      label="membership-category"
                      onChange={(e) => setMembershipCategory(e.target.value)}
                      placeholder={membershipCategory}
                      value={membershipCategory}
                    />
                  </FormField>
                  <FormField>
                    <StyledTextInput
                      data-testid="population-input"
                      label="population"
                      onChange={(e) => setPopulation(e.target.value)}
                      placeholder={population}
                      value={population}
                    />
                  </FormField>
                  <FormField>
                    <StyledTextInput
                      data-testid="client-name-input"
                      label="client-name"
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder={clientName}
                      value={clientName}
                    />
                  </FormField>
                  <FormField>
                    <StyledTextInput
                      data-testid="billing-codes-input"
                      label="billing-codes"
                      onChange={(e) => setBillingCodes(e.target.value)}
                      placeholder={billingCodes}
                      value={billingCodes}
                    />
                  </FormField>
                </SubSection>
              </Section>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion>

        {/* Portal Configuration Section wrapped in an Accordion (closed by default) */}
        <Accordion defaultValue={undefined} isCollapsible type="single">
          <Accordion.Item value="portal-configuration">
            <Accordion.Trigger>
              <Accordion.Header>
                <SectionTitle>Portal Configuration</SectionTitle>
              </Accordion.Header>
            </Accordion.Trigger>
            <Accordion.Content>
              <Section>
                {/* Portal Selection (rendered inline for quick access) */}
                <FormField>
                  <FormFieldLabel>Portal Name</FormFieldLabel>
                  <RadioGroupWrapper>
                    <StyledRadioGroup
                      data-testid="portal-name-radio"
                      display="column"
                      label="Portal Name"
                      onChange={(e) => handlePortalChange(e.target.value)}
                      value={portalName}
                    >
                      <StyledRadio label="UHC (Standard)" value="uhc" />
                      <StyledRadio label="LAWW (Behavioral)" value="laww" />
                      <StyledRadio label="UMR" value="umr" />
                      <StyledRadio label="GEHA" value="geha" />
                    </StyledRadioGroup>
                  </RadioGroupWrapper>
                </FormField>

                {/* Theme & Base Path - informational fields */}
                <FormField>
                  <FormFieldLabel>Theme (determined by Portal)</FormFieldLabel>
                  <StyledText>
                    <strong>{theme === 'optum' ? 'Optum' : 'UHC'}</strong>
                  </StyledText>
                  <SmallText>
                    Theme is automatically set based on portal selection. LAWW
                    uses Optum theme; all others use UHC theme.
                  </SmallText>
                </FormField>

                <FormField>
                  <FormFieldLabel>
                    Base Path (determined by Portal)
                  </FormFieldLabel>
                  <StyledText>
                    <strong>{basePath}</strong>
                  </StyledText>
                  <SmallText>
                    Base path is automatically set based on portal selection:
                    UHC → /connect, LAWW → /laww, UMR → /umr, GEHA → /geha
                  </SmallText>
                </FormField>

                {/* Language selection */}
                <FormField>
                  <FormFieldLabel>Language</FormFieldLabel>
                  <RadioGroupWrapper>
                    <StyledRadioGroup
                      data-testid="language-radio"
                      display="row"
                      label="Language"
                      onChange={(e) => setLanguage(e.target.value)}
                      value={language}
                    >
                      <StyledRadio label="English" value="en-US" />
                      <StyledRadio label="Spanish" value="es-US" />
                    </StyledRadioGroup>
                  </RadioGroupWrapper>
                </FormField>

                {/* Fallback LOB (only when LAWW selected) */}
                {portalName === 'laww' && (
                  <FormField>
                    <StyledToggleSwitch
                      data-testid="use-fallback-lob-toggle"
                      helper="Uses ENI Lagoon data instead of OBH. This is enabled by default for LAWW because OBH Lagoon data typically doesn't exist in dev environments. The widget will use the Optum theme but load ENI configuration data."
                      isChecked={useFallbackLob}
                      label="Use Fallback LOB (ENI instead of OBH)"
                      onChange={(e) => {
                        setUseFallbackLob(e.target.checked);
                        setWidgetKey((prev) => prev + 1);
                      }}
                    />
                    {useFallbackLob && (
                      <SmallText css={{ color: '#d97706', marginTop: '4px' }}>
                        ⚠️ Using ENI Lagoon data. Widget appearance reflects
                        LAWW/Optum theme, but feature flags come from ENI
                        configuration.
                      </SmallText>
                    )}
                  </FormField>
                )}
              </Section>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion>

        {/* Developer Tools wrapped in an Accordion (closed by default) */}
        <Accordion defaultValue={undefined} isCollapsible type="single">
          <Accordion.Item value="developer-tools">
            <Accordion.Trigger>
              <Accordion.Header>
                <SectionTitle>Developer Tools</SectionTitle>
              </Accordion.Header>
            </Accordion.Trigger>
            <Accordion.Content>
              <Section>
                <Accordion type="multiple">
                  {/* Loading State Debugger */}
                  <Accordion.Item value="loading-debugger">
                    <Accordion.Trigger>
                      <Accordion.Header>
                        🔄 Loading State Debugger
                      </Accordion.Header>
                    </Accordion.Trigger>
                    <Accordion.Content>
                      <SmallText
                        css={{ marginBottom: '12px', color: '#6b7280' }}
                      >
                        Simulate loading states to debug skeleton appearances.
                        Only the preferences loading skeleton is shown for
                        widgets.
                      </SmallText>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px',
                        }}
                      >
                        {(
                          Object.keys(loadingStateConfigs) as LoadingStateType[]
                        ).map((stateKey) => {
                          const config = loadingStateConfigs[stateKey];
                          const isActive = simulatedLoadingState === stateKey;
                          return (
                            <Button
                              key={stateKey}
                              onClick={() => setSimulatedLoadingState(stateKey)}
                              size="sm"
                              variant={isActive ? 'solid' : 'outline'}
                            >
                              {config.label}
                              {stateKey !== 'none' && (
                                <span
                                  style={{
                                    fontSize: '10px',
                                    marginLeft: '8px',
                                    opacity: 0.7,
                                  }}
                                >
                                  {config.skeletonWidth}×{config.skeletonHeight}
                                  px
                                </span>
                              )}
                            </Button>
                          );
                        })}
                      </div>
                      {simulatedLoadingState !== 'none' && (
                        <SmallText
                          css={{
                            marginTop: '10px',
                            padding: '8px',
                            backgroundColor: '#fffbeb',
                            borderRadius: '4px',
                            color: '#92400e',
                          }}
                        >
                          <strong>Source:</strong>{' '}
                          {loadingStateConfigs[simulatedLoadingState].source}
                        </SmallText>
                      )}
                    </Accordion.Content>
                  </Accordion.Item>

                  {/* Current Props */}
                  <Accordion.Item value="current-props">
                    <Accordion.Trigger>
                      <Accordion.Header>Current Props (JSON)</Accordion.Header>
                    </Accordion.Trigger>
                    <Accordion.Content>
                      <PropsDisplay>
                        {JSON.stringify(getCurrentProps(), null, 2)}
                      </PropsDisplay>
                    </Accordion.Content>
                  </Accordion.Item>

                  {/* CEX Integration Example */}
                  <Accordion.Item value="cex-example">
                    <Accordion.Trigger>
                      <Accordion.Header>
                        CEX Integration Example
                      </Accordion.Header>
                    </Accordion.Trigger>
                    <Accordion.Content>
                      <PropsDisplay>
                        {`<smart-choice-widget-app
  is-super-user="${isSuperUser}"
  is-full-screen-compare-smart-choice="${isFullScreenCompareSmartChoice}"
  is-mixed-results-page="${isMixedResultsPage}"
  show-choose-pcp-header="${showChoosePCPHeader}"
  show-member-selection="${showMemberSelection}"
  portal-data='${JSON.stringify(portalData)}'
/>`}
                      </PropsDisplay>
                    </Accordion.Content>
                  </Accordion.Item>
                </Accordion>
              </Section>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion>
      </ControlPanel>

      {/* Widget Display - Right Side */}
      <WidgetContainer>
        <StyledHeading offset={5}>Widget Preview</StyledHeading>
        <StyledText>
          isSuperUser: <strong>{isSuperUser ? 'true' : 'false'}</strong> |
          Portal: <strong>{portalName}</strong> | Theme:{' '}
          <strong>{theme}</strong>
          {lastWidgetEvent && (
            <>
              {' '}
              | User: <strong>{effectiveUserId}</strong>
            </>
          )}
        </StyledText>

        {/* Mock State Summary */}
        <MockStateSummary>
          <MockStateSummaryTitle>🧪 Active Mock State</MockStateSummaryTitle>
          <MockStateBadgeContainer>
            {(
              Object.entries(mockPreferences) as [
                keyof MockPreferences,
                boolean
              ][]
            ).map(([key, value]) => (
              <MockStateBadge active={value} key={key}>
                {key}: {value ? '✓' : '✗'}
              </MockStateBadge>
            ))}
            {/* Simulating Loading State indicator */}
            <MockStateBadge
              active={simulatedLoadingState !== 'none'}
              key="simulatingLoading"
            >
              simulatingLoading: {simulatedLoadingState !== 'none' ? '✓' : '✗'}
            </MockStateBadge>
          </MockStateBadgeContainer>
        </MockStateSummary>

        {/* Widget Event Indicator - demonstrates parent app listening to widget events */}
        {lastWidgetEvent && (
          <MockStateSummary>
            <MockStateSummaryTitle>
              📡 Last Widget Event (Parent App Integration Demo)
            </MockStateSummaryTitle>
            <MockStateBadgeContainer>
              <MockStateBadge
                active={lastWidgetEvent.type === 'PREFERENCES_LOADED'}
              >
                Type: {lastWidgetEvent.type}
              </MockStateBadge>
              <MockStateBadge active={lastWidgetEvent.source === 'api-save'}>
                Source: {lastWidgetEvent.source}
              </MockStateBadge>
              <MockStateBadge active={false}>
                Time: {new Date(lastWidgetEvent.timestamp).toLocaleTimeString()}
              </MockStateBadge>
            </MockStateBadgeContainer>
            <SmallText css={{ marginTop: '8px', color: '#6b7280' }}>
              This demonstrates how CEX can listen to widget events and save
              preferences to Redux. Check console for full event details.
            </SmallText>
          </MockStateSummary>
        )}

        <WidgetWrapper>
          {simulatedLoadingState !== 'none' ? (
            <Skeleton
              height={loadingStateConfigs[simulatedLoadingState].skeletonHeight}
              variant="rounded"
              width={loadingStateConfigs[simulatedLoadingState].skeletonWidth}
            />
          ) : (
            <SmartChoiceWidgetApp
              breadcrumbs={[]}
              flex-css={JSON.stringify({ gap: '0 10px' })}
              is-full-screen-compare-smart-choice={
                isFullScreenCompareSmartChoice
              }
              is-mixed-results-page={isMixedResultsPage}
              is-super-user={isSuperUser}
              key={widgetKey}
              member-id={memberId || undefined}
              portal-data={JSON.stringify(portalData)}
              show-choose-pcp-header={showChoosePCPHeader}
              show-member-selection={showMemberSelection}
              submit-button-label={submitButtonLabel || undefined}
            />
          )}
          <span>
            <Button
              data-testid="launch-details-page-button"
              onClick={() => {
                setShowDetailsPage(true);
              }}
              variant="outline"
              css={{
                marginTop: '16px',
              }}
            >
              Open Smartchoice Details Drawer
            </Button>
            {showDetailsPage && (
              <SmartChoiceWidgetApp
                breadcrumbs={[]}
                flex-css={JSON.stringify({ gap: '0 10px' })}
                is-full-screen-compare-smart-choice={
                  isFullScreenCompareSmartChoice
                }
                is-mixed-results-page={isMixedResultsPage}
                is-super-user={isSuperUser}
                show-details-page={showDetailsPage}
                key={widgetKey}
                member-id={memberId || undefined}
                portal-data={JSON.stringify(portalData)}
                show-choose-pcp-header={showChoosePCPHeader}
                show-member-selection={showMemberSelection}
                submit-button-label={submitButtonLabel || undefined}
                provider-id={providerId || undefined}
                provider-type={providerType || undefined}
                coverage-type={coverageType || undefined}
                claim-system-type-code={claimSystemTypeCode || undefined}
                eligibility-system-type-code={
                  eligibilitySystemTypeCode || undefined
                }
                latitude={latitude || undefined}
                lob={lob || undefined}
                location-id={locationId || undefined}
                longitude={longitude || undefined}
                plan-variation-code={planVariationCode || undefined}
                plan-year={planYear || undefined}
                policy-id={policyId || undefined}
                reciprocity-id={reciprocityId || undefined}
                membership-category={membershipCategory || undefined}
                population={population || undefined}
                client-name={clientName || undefined}
                billing-codes={billingCodes || undefined}
                ces-product-code={cesProductCode || undefined}
                market-type={marketType || undefined}
              />
            )}
          </span>
        </WidgetWrapper>

        {/* Event Log Section - Simulates Redux DevTools / Action Log */}
        <EventLogSection>
          <EventLogHeader>
            <EventLogTitle>
              📋 Widget Event Log (Redux Actions for CEX)
            </EventLogTitle>
            <ClearLogButton onClick={() => setEventLog([])}>
              Clear Log
            </ClearLogButton>
          </EventLogHeader>
          <SmallText
            css={{ color: '#9ca3af', marginBottom: '12px', display: 'block' }}
          >
            These events can be consumed by CEX to update its Redux store. Each
            event represents an action that would be dispatched.
          </SmallText>
          <EventLogList>
            {eventLog.length === 0 ? (
              <EventLogEmpty>
                No events yet. Interact with the widget to see events appear
                here.
              </EventLogEmpty>
            ) : (
              eventLog.map((event) => (
                <EventLogItem
                  eventType={
                    event.type as
                      | 'PREFERENCES_LOADED'
                      | 'PREFERENCES_SAVED'
                      | 'PREFERENCES_ERROR'
                  }
                  key={event.id}
                >
                  <EventLogAction>
                    {event.action}
                    <EventLogTimestamp>
                      {new Date(event.timestamp).toLocaleTimeString()}
                    </EventLogTimestamp>
                  </EventLogAction>
                  <EventLogPayload>
                    {JSON.stringify(event.payload, null, 2)}
                  </EventLogPayload>
                </EventLogItem>
              ))
            )}
          </EventLogList>
        </EventLogSection>
      </WidgetContainer>
    </Container>
  );
};

export default SmartChoiceDemo;
